import {
  getDatadogEnv,
  getDatadogRumApplicationId,
  getDatadogRumClientToken,
  getDatadogRumSessionSampleRate,
  getDatadogRumSessionReplaySampleRate,
  getDatadogService,
  getDatadogSite,
  getDatadogVersion,
  isDatadogLogsConfigured,
  isDatadogRumConfigured,
  isE2eTestMode,
} from "@/lib/datadog/config";
import { getRumAllowedTracingUrls } from "@/lib/datadog/rumAllowedTracingUrls";
import { DATADOG_RUM_READY_EVENT } from "@/datadogRumReady";

function shouldInitDatadogClient(): boolean {
  return !isE2eTestMode() && (isDatadogRumConfigured() || isDatadogLogsConfigured());
}

// DatadogAppRouter (mounted in layout.tsx via DatadogRumRouter) calls startNextjsView() on its
// first render, but that races with the dynamic imports below — if it fires before datadogRum.init()
// has run, nextjsPlugin's startNextjsView() silently no-ops with no retry, permanently losing the
// first view of a cold page load. DatadogRumRouter waits for DATADOG_RUM_READY_EVENT before
// rendering the router; that constant lives in its own side-effect-free module so importing it
// cannot pull this file into a server bundle. See datadogRumReady.ts.

function signalDatadogRumReady(): void {
  window.__datadogRumReady = true;
  window.dispatchEvent(new Event(DATADOG_RUM_READY_EVENT));
}

let initialized = false;

async function initDatadogClient(): Promise<void> {
  if (initialized || !shouldInitDatadogClient()) {
    signalDatadogRumReady();
    return;
  }
  initialized = true;

  const datadogClientConfig = {
    clientToken: getDatadogRumClientToken()!,
    site: getDatadogSite(),
    service: getDatadogService(),
    env: getDatadogEnv(),
    version: getDatadogVersion(),
  };

  if (isDatadogLogsConfigured()) {
    const { datadogLogs } = await import("@datadog/browser-logs");
    datadogLogs.init({
      ...datadogClientConfig,
      forwardErrorsToLogs: true,
      sessionSampleRate: getDatadogRumSessionSampleRate(),
    });
  }

  if (isDatadogRumConfigured()) {
    const [{ datadogRum }, { nextjsPlugin }] = await Promise.all([
      import("@datadog/browser-rum"),
      import("@datadog/browser-rum-nextjs"),
    ]);

    datadogRum.init({
      applicationId: getDatadogRumApplicationId()!,
      ...datadogClientConfig,
      sessionSampleRate: getDatadogRumSessionSampleRate(),
      sessionReplaySampleRate: getDatadogRumSessionReplaySampleRate(),
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      plugins: [nextjsPlugin()],
      allowedTracingUrls: getRumAllowedTracingUrls(),
    });
  }

  signalDatadogRumReady();
}

// Guarded even though this is a client-only entrypoint. Extracting DATADOG_RUM_READY_EVENT removed
// the import that dragged this file into the server bundle, but nothing stops the next one, and the
// failure mode was quiet: a voided promise rejecting during SSR logs an unhandled rejection rather
// than failing the render, so it went unnoticed while flooding the CI and production logs.
if (typeof window !== "undefined") {
  void initDatadogClient();
}

type RouterTransitionHandler = (
  href: string,
  navigationType: "push" | "replace" | "traverse",
) => void | Promise<void>;

export const onRouterTransitionStart: RouterTransitionHandler = async (href) => {
  if (!isDatadogRumConfigured() || isE2eTestMode()) {
    return;
  }

  const { onRouterTransitionStart: trackRouterTransition } = await import(
    "@datadog/browser-rum-nextjs"
  );
  return trackRouterTransition(href);
};
